// DS: MO 4.1.5
import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, RelatedBlock, PapiUser } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { BiContextProvidedValue, BiData, DreamupInfoClickBiEvent } from '@wix/da-bi/pkg/events';
import FragmentsIcon from '@wix/da-ds/pkg/Icons/24x24/Fragments';
import DaDreamupLogoAndText from '@wix/da-ds/pkg/DaDreamupLogoAndText';
import { first } from '../../../../utils/fp';
import DeviationPromo from '../DeviationPromo';
import CreateButton from './CreateButton';
import BasicUserName from '../../../../User/BasicUserName/BasicUserName';

import s from './DreamupPromo.scss';

interface Props {
  block: RelatedBlock;
}

interface AttributionUserNameProps {
  user: PapiUser | undefined;
  className?: string;
}

const AttributionUserName: React.FC<AttributionUserNameProps> = ({ user, className }) => {
  if (!user) return null;
  return (
    <span
      className={className}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        window.location.href = Url.userLink(user.username);
      }}
      style={{ cursor: 'pointer' }}
    >
      <BasicUserName user={user} showSymbol={false} />
    </span>
  );
};

const DreamupPromo: React.FC<Props> = ({ block: { contentType, deviations = [] } }) => {
  const { t } = useTranslation();
  const deviation: PapiDeviation | undefined = first(deviations);
  return (
    <DeviationPromo
      deviation={deviation}
      href={
        deviation && contentType === 'monetization_promotion_dreamup_thirdparty'
          ? Url.dreamupLink({ deviationid: deviation.deviationId })
          : Url.dreamupLink()
      }
      biData={
        deviation &&
        BiData<DreamupInfoClickBiEvent>({
          evid: 923,
          typeid: deviation.typeId,
          itemid: deviation.deviationId,
          sectionname: BiContextProvidedValue,
        })
      }
      heading={t('sidebar.dreamupPromo.heading')}
      subheading={t('sidebar.dreamupPromo.description')}
      attributionSlot={
        deviation &&
        deviation.author &&
        t('sidebar.dreamupPromo.attribution', {
          postProcess: 'reactPostprocessor',
          userLink: (
            <AttributionUserName
              user={deviation.author}
              className={c(s['attribution-user-link'])}
            />
          ),
        })
      }
      logoSlot={
        <DaDreamupLogoAndText
          className={s['logo']}
          aria-label={t('sidebar.dreamupPromo.logo.a11y.label')}
          view="light"
        />
      }
      ctaSlot={
        <CreateButton
          icon={FragmentsIcon}
          // This should be removed from the tab order since it isn't a link or a button, we're just using the styles at
          // this point.
          aria-hidden="true"
          tabIndex={-1}
        >
          {t('sidebar.dreamupPromo.button.label')}
        </CreateButton>
      }
    />
  );
};

DreamupPromo.displayName = 'DreamupPromo';

export default DreamupPromo;
